import React, { FormEvent, useState, useEffect, ChangeEventHandler } from 'react';
import { styled, makeStyles, Modal, Backdrop, Typography, Button, Box, RadioGroup, FormLabel, FormControlLabel, Radio, TextField, MenuItem, ButtonGroup } from '@material-ui/core';
import Swal from 'sweetalert2';
import { TagModel, TagType } from '@/models/tag';
import { apiClient } from '@/api/client';
import { toastConfig } from '@/services/utils/swalHelper';
import { CustomerTagModel } from '@/models/customer';

interface Error {
	field: string
	message: string
}

interface Props {
	customerId: string
	show: boolean
	onClose: () => void
	onSuccess: (note: CustomerTagModel) => void
}

const useStyles = makeStyles(() => ({
	wrapper: {
		zIndex: '1 !important' as any,
	},
	modalBackdrop: {
		zIndex: 3,
	},
	buttonGroup: {
		marginTop: '20px',
		float: 'right',
		'& .MuiButtonBase-root': {
			marginLeft: '15px',
			borderRadius: '4px',
			width: '66px',
			height: '36px',
			'& .MuiButton-label': {
				fontSize: '13px',
			},
		},
	},
	subCaption: {
		marginTop: '20px',
	},
	input: {
		marginTop: '5px'
	},
	paragraph: {
		fontSize: '14px',
	},
	noteContent: {
		'& .MuiInputBase-input': {
			minHeight: '70px',
			maxHeight: '70px',
		},
	},
}));

const modalContentStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 400,
	bgcolor: 'background.paper',
	zIndex: 10,
	border: '2px',
	borderRadius: '1%',
	boxShadow: 24,
	p: 4,
};

const StyledBackdrop = styled(Backdrop)({
	zIndex: 9
});

type TagTypeText = 'classification' | 'experience';

export const AddNote = (props: Props) => {
	const classes = useStyles();
	const [note, setNote] = useState('');
	const [tagId, setTagId] = useState('');
	const [tags, setTags] = useState<TagModel[]>([]);
	const [tagType, setTagType] = useState(TagType.CLASSIFICATION);
	const [tagTypeText, setTagTypeText] = useState<TagTypeText>('classification');
	const [isReady, setIsReady] = useState(false);
	const [errors, setErrors] = useState<Error[]>([]);

	useEffect(() => {
		const text = tagType === TagType.CLASSIFICATION ? 'classification' : 'experience';

		setTagId('');
		setTagTypeText(text);
	}, [tagType]);

	useEffect(() => {
		async function fetchTags() {
			const tags = await apiClient.customer.getTagsForNotes();

			setTags(tags);
			setIsReady(true);
		}

		fetchTags();
	}, []);

	const handleTypeChange : ChangeEventHandler<HTMLInputElement> = (event) => {
		const type = event.target.value === 'classification' ? TagType.CLASSIFICATION : TagType.EXPERIENCE;

		setTagType(type);
	}

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();

		const validate = () => {
			const errors = [];

			if (!tagId) {
				errors.push({
					field: 'tagId',
					message: 'Please select a tag'
				});
			}

			if (note.length === 0) {
				errors.push({
					field: 'note',
					message: 'Note is required'
				});
			}

			return errors;
		};

		const errors = validate();

		if (errors.length > 0) {
			setErrors(errors);
			return;
		}

		setErrors([]);

		try {
			const result = await apiClient.customer.addCustomerTag({
				note: note,
				customerId: props.customerId,
				tagId: tagId
			});

			Swal.fire({
				icon: 'success',
				text: 'Record created successfully',
				...toastConfig(),
			});

			props.onSuccess({ ...result });

			closeModal();
		} catch (error) {
			Swal.fire({
				icon: 'error',
				title: 'Something went wrong',
				text: 'An error occurred during creating record',
			});
		}
	};

	const getError = (field: string) => {
		return errors.find(e => e.field === field);
	};

	function resetForm() : void {
		setNote('');
		setTagId('');
		setTagType(TagType.CLASSIFICATION);
		setErrors([]);
	}

	function closeModal() {
		resetForm()

		props.onClose();
	}

	if (!isReady) {
		return null;
	}

	return (
		<div data-id="modalAddNote">
			<Modal
				open={props.show}
				onClose={closeModal}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				className={classes.wrapper}
				BackdropComponent={StyledBackdrop}
			>
				<form noValidate autoComplete="off" onSubmit={handleSubmit}>
					<Box sx={modalContentStyle}>
						<Typography variant="h3" component="h3">
							Add classification and note
						</Typography>
						<FormLabel className={classes.subCaption} component="legend">
							<Typography variant="h6" component="h6">
								Choose type
						</Typography>
						</FormLabel>
						<RadioGroup
							defaultValue="classification"
							name="radio-buttons-group"
							row
							onChange={handleTypeChange}
						>
							<FormControlLabel value="classification" control={<Radio color="primary" />} label="Guest classification" />
							<FormControlLabel value="experience" control={<Radio color="primary" />} label="Guest experience" />
						</RadioGroup>

						<FormLabel className={classes.subCaption} component="legend">
							<Typography variant="h6" component="h6">
								Add {tagTypeText}
							</Typography>
						</FormLabel>

						<TextField
							name="tagId"
							select
							value={tagId}
							label="Classification"
							variant="outlined"
							fullWidth
							className={classes.subCaption}
							error={!!getError('tagId')}
							helperText={getError('tagId')?.message}
							onChange={e => setTagId(e.target.value)}
						>
							{tags.filter(t => t.type === tagType).map(tag => (
								<MenuItem key={tag.id} value={tag.id}>
									{tag.label}
								</MenuItem>
							))}

						</TextField>
						<FormLabel className={classes.subCaption} component="legend">
							<Typography variant="h6" component="h6">
								Add note
							</Typography>
						</FormLabel>
						<p className={classes.paragraph}>Describe why did you add the above classification to the guest. This will give important context for your colleagues.</p>

						<TextField
							name="note"
							type="text"
							value={note}
							label="Note"
							maxRows={15}
							multiline
							fullWidth
							variant="outlined"
							inputProps={{ maxLength: 5000 }}
							onChange={e => setNote(e.currentTarget.value)}
							className={[classes.input, classes.noteContent].join(' ')}
							error={!!getError('note')}
							helperText={getError('note')?.message}
						/>

						<ButtonGroup
							orientation="horizontal"
							size="small"
							className={classes.buttonGroup}
						>
							<Button type="button" name="cancelCustomerTag" variant="text" onClick={closeModal}>Cancel</Button>
							<Button type="submit" name="submitCustomerTag" variant="contained" color="primary">Save</Button>
						</ButtonGroup>
					</Box>
				</form>
			</Modal>
		</div>
	);
};
